import React, { useState, useRef } from 'react';
import { useQuery, useMutation, gql, useApolloClient } from '@apollo/client';
import {
  Paperclip,
  Upload,
  Download,
  Trash2,
  FileText,
  FileImage,
  File as FileIcon,
  Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';

export const GET_ATTACHMENTS = gql`
  query GetAttachments($entityType: String!, $entityId: String!) {
    attachments(entityType: $entityType, entityId: $entityId) {
      id
      filename
      mimeType
      sizeBytes
      providerType
      createdAt
      uploadedBy {
        id
        firstName
        lastName
      }
    }
  }
`;

export const ATTACHMENT_DOWNLOAD_URL = gql`
  query AttachmentDownloadUrl($id: String!) {
    attachmentDownloadUrl(id: $id)
  }
`;

export const DELETE_ATTACHMENT = gql`
  mutation DeleteAttachment($id: String!) {
    deleteAttachment(id: $id)
  }
`;

interface AttachmentSectionProps {
  entityType: 'TASK' | 'LEAVE' | 'EMPLOYEE' | 'PROJECT';
  entityId: string;
  /** Hide the upload control if the user can't add files. */
  canUpload?: boolean;
  /** Show delete buttons (still server-enforced). */
  canDelete?: boolean;
}

interface AttachmentRow {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  providerType: string;
  createdAt: string;
  uploadedBy: { id: string; firstName: string; lastName: string };
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getIcon = (mimeType: string) => {
  if (mimeType.startsWith('image/')) return FileImage;
  if (mimeType === 'application/pdf' || mimeType.startsWith('text/')) return FileText;
  return FileIcon;
};

const getApiBase = (): string => {
  const graphqlUrl = process.env.REACT_APP_GRAPHQL_URL;
  if (graphqlUrl) {
    try {
      const u = new URL(graphqlUrl);
      return `${u.protocol}//${u.host}`;
    } catch {
      // fall through
    }
  }
  return '';
};

export const AttachmentSection: React.FC<AttachmentSectionProps> = ({
  entityType,
  entityId,
  canUpload = true,
  canDelete = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AttachmentRow | null>(null);
  const apollo = useApolloClient();

  const { data, loading, refetch } = useQuery<{ attachments: AttachmentRow[] }>(
    GET_ATTACHMENTS,
    {
      variables: { entityType, entityId },
      fetchPolicy: 'cache-and-network',
      skip: !entityId,
    },
  );

  const [deleteAttachment, { loading: deleting }] = useMutation(DELETE_ATTACHMENT);

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('entityType', entityType);
      fd.append('entityId', entityId);

      const apiBase = getApiBase();
      const res = await fetch(`${apiBase}/api/attachments/upload`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `Upload nieudany (${res.status})`);
      }
      toast.success(`Plik "${file.name}" dodany`);
      await refetch();
    } catch (err: any) {
      toast.error(err.message || 'Nie udało się przesłać pliku');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (att: AttachmentRow) => {
    try {
      const result = await apollo.query<{ attachmentDownloadUrl: string }>({
        query: ATTACHMENT_DOWNLOAD_URL,
        variables: { id: att.id },
        fetchPolicy: 'network-only',
      });
      const url = result.data.attachmentDownloadUrl;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      toast.error(err.message || 'Nie udało się pobrać pliku');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteAttachment({ variables: { id: confirmDelete.id } });
      toast.success(`Plik "${confirmDelete.filename}" usunięty`);
      setConfirmDelete(null);
      await refetch();
    } catch (err: any) {
      toast.error(err.message || 'Nie udało się usunąć pliku');
    }
  };

  const attachments = data?.attachments ?? [];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Paperclip className="w-4 h-4" />
          Załączniki
          {attachments.length > 0 && (
            <span className="text-xs text-muted-foreground">
              ({attachments.length})
            </span>
          )}
        </div>
        {canUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelected}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleUploadClick}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-1" />
              )}
              {uploading ? 'Przesyłanie...' : 'Dodaj plik'}
            </Button>
          </>
        )}
      </div>

      {loading && attachments.length === 0 ? (
        <div className="text-xs text-muted-foreground">Ładowanie...</div>
      ) : attachments.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">
          Brak załączników
        </div>
      ) : (
        <ul className="space-y-1">
          {attachments.map((a) => {
            const Icon = getIcon(a.mimeType);
            return (
              <li
                key={a.id}
                className="flex items-center gap-2 text-sm border rounded-md px-2 py-1.5 bg-card"
              >
                <Icon className="w-4 h-4 shrink-0 text-muted-foreground" />
                <button
                  type="button"
                  onClick={() => handleDownload(a)}
                  className="flex-1 text-left truncate hover:text-primary"
                  title={a.filename}
                >
                  {a.filename}
                </button>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatSize(a.sizeBytes)}
                </span>
                <span className="text-xs text-muted-foreground shrink-0 hidden md:inline">
                  {a.uploadedBy.firstName} {a.uploadedBy.lastName}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDownload(a)}
                  title="Pobierz"
                  className="h-7 w-7"
                >
                  <Download className="w-4 h-4" />
                </Button>
                {canDelete && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setConfirmDelete(a)}
                    title="Usuń"
                    className="h-7 w-7 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Dialog
        open={!!confirmDelete}
        onOpenChange={(v) => {
          if (!v) setConfirmDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Usunąć załącznik?</DialogTitle>
            <DialogDescription>
              Plik <strong>{confirmDelete?.filename}</strong> zostanie trwale
              usunięty z systemu i z magazynu plików. Tej operacji nie można
              cofnąć.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              disabled={deleting}
            >
              Anuluj
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
